import { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import useAnalyticsStore from '../stores/analyticsStore';

// Event detail drawer ([6.6]). Renders entirely from the selected row already in
// the store's events array (EVENT_SELECT carries every field) - no fetch, no
// detail endpoint. The VMS clip link is deferred to spike 868jxu8mu (analytics
// cameraId has no validated mapping to a VMS handle), so no link is rendered.

function formatTimestamp(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

// A labeled text row, omitted entirely when the value is empty.
function Field({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="analytics-detail-field">
      <span className="analytics-detail-field-label">{label}</span>
      <span className="analytics-detail-field-value">{value}</span>
    </div>
  );
}

// A pretty-printed JSON block, omitted when the structured value is null/empty.
function JsonBlock({ label, value }) {
  const isEmpty =
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);
  if (isEmpty) return null;
  return (
    <div className="analytics-detail-json">
      <span className="analytics-detail-field-label">{label}</span>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}

// A section wrapper that renders nothing when all its children are null.
function Section({ title, children }) {
  const hasContent = Array.isArray(children)
    ? children.some(Boolean)
    : Boolean(children);
  if (!hasContent) return null;
  return (
    <div className="analytics-detail-section">
      <div className="analytics-detail-section-title">{title}</div>
      {children}
    </div>
  );
}

const AnalyticsEventDetail = () => {
  const events = useAnalyticsStore((s) => s.events);
  const selectedEventId = useAnalyticsStore((s) => s.selectedEventId);
  const clearSelection = useAnalyticsStore((s) => s.clearSelection);
  const cameraNames = useAnalyticsStore((s) => s.cameraNames);
  const cameraLabel = useAnalyticsStore((s) => s.cameraLabel);

  // selector over the already-loaded rows; cameraNames in deps so the label
  // refreshes once names load. (cameraNames referenced to satisfy the linter
  // and to re-resolve the header label after fetchCameraNames resolves.)
  const ev = useMemo(
    () => events.find((e) => e.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  // Esc to close, only while a row is selected.
  useEffect(() => {
    if (!ev) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ev, clearSelection]);

  if (!ev) return null;

  const label = cameraLabel(ev.cameraId);
  const showRawId = label !== ev.cameraId; // only when a name actually resolved
  void cameraNames; // subscription dependency for label reactivity

  const skew =
    ev.clockSkewMs === null || ev.clockSkewMs === undefined
      ? null
      : `clock skew ${ev.clockSkewMs} ms`;

  return (
    <>
      <div className="analytics-detail-scrim" onClick={clearSelection} />
      <aside className="analytics-detail-drawer" role="dialog" aria-label="Event detail">
        <div className="analytics-detail-head">
          <div className="analytics-detail-camera">
            <span className="analytics-detail-camera-name">{label}</span>
            {showRawId && <span className="analytics-detail-camera-id">{ev.cameraId}</span>}
            <span className="analytics-detail-received">{formatTimestamp(ev.receivedAt) || '-'}</span>
          </div>
          <button
            type="button"
            className="analytics-detail-close"
            onClick={clearSelection}
            aria-label="Close detail"
          >
            <X size={18} />
          </button>
        </div>

        <div className="analytics-detail-body">
          <Section title="Identity">
            <Field label="Canonical topic" value={ev.canonicalTopic} />
            <Field label="Vendor topic" value={ev.vendorTopic} />
            <Field label="Vendor" value={ev.vendor} />
            <Field label="Category" value={ev.eventCategory} />
            <Field label="Property operation" value={ev.propertyOperation} />
          </Section>

          <Section title="Timing">
            <Field label="Received" value={formatTimestamp(ev.receivedAt)} />
            <Field label="Camera reported" value={formatTimestamp(ev.cameraReportedAt)} />
            <Field label="Clock skew" value={skew} />
          </Section>

          <Section title="Source / data">
            <Field label="Source key" value={ev.sourceKey} />
            <Field label="Source value" value={ev.sourceValue} />
            <Field label="Data key" value={ev.dataKey} />
            <Field label="Data value" value={ev.dataValue} />
          </Section>

          <Section title="Classification">
            <Field label="Vendor classification" value={ev.vendorClassification} />
            <Field label="Zone" value={ev.zoneName} />
          </Section>

          <Section title="Structured">
            <JsonBlock label="source" value={ev.source} />
            <JsonBlock label="data" value={ev.data} />
            <JsonBlock label="elements" value={ev.elements} />
          </Section>
        </div>
      </aside>
    </>
  );
};

export default AnalyticsEventDetail;
